/**
 * The design system, built on the generated tokens.
 *
 * `generated.ts` holds the raw values lifted from the mockup stylesheet. This
 * file gives them meaning — which colour is a button, what a tab bar looks like,
 * how far apart things sit. Splitting it that way means regenerating the tokens
 * never overwrites a decision, and changing a decision never invents a colour
 * that the mockups do not have.
 */
import { colors, fontSize, radius } from './generated.js';
/** The spacing ladder. Every gap in the app is one of these. */
export const space = {
    xs: 4,
    sm: 7,
    md: 11,
    lg: 14,
    xl: 20,
    xxl: 26,
};
/**
 * Semantic colours.
 *
 * Components use these names, never the raw palette: `danger` can be retuned
 * without hunting for every `flame` in the codebase, and a reviewer can see that
 * a screen is using "the wrong red" rather than having to know the palette.
 */
export const semantic = {
    background: colors.bg,
    surface: colors.card,
    text: colors.ink,
    textMuted: colors.muted,
    border: colors.line,
    /** Progress, success, and the primary action. */
    primary: colors.green,
    primaryPressed: colors.greenD,
    /** Sneakers, the tab bar's active pill, and anything informational. */
    accent: colors.navy2,
    accentSoft: colors.sky,
    /** Acorns, chests, and completed path nodes. */
    reward: colors.gold,
    /** The streak flame, and hard mode. */
    streak: colors.flame,
    rare: colors.purple,
};
/**
 * There is no `error` or `danger` colour, and that is deliberate.
 *
 * A wrong answer is never red, never has a failure sound, and never takes
 * anything away ([Lesson types](../../../docs/LESSON-TYPES.md)). Giving the
 * palette a red would make it available, and the first tired evening someone
 * would use it.
 */
export const NO_DANGER_COLOUR = true;
/** The five bottom tabs, in order (D-027). */
export const TABS = [
    { key: 'learn', icon: 'tab-learn', label: 'Learn' },
    { key: 'quests', icon: 'tab-quests', label: 'Quests' },
    { key: 'shop', icon: 'tab-shop', label: 'Shop' },
    { key: 'letters', icon: 'tab-letters', label: 'Letters & Words' },
    { key: 'profile', icon: 'tab-profile', label: 'Profile' },
];
/**
 * Labels exist for screen readers, not for the eye.
 *
 * The bar is icons only, because a child who cannot yet read English should be
 * able to navigate it ([Accessibility](../../../docs/ACCESSIBILITY.md)). The
 * label is what VoiceOver announces.
 */
export const TAB_LABELS_ARE_FOR_ASSISTIVE_TECH = true;
export const typography = {
    title: { fontSize: fontSize.h1, fontWeight: '800', letterSpacing: -0.6 },
    heading: { fontSize: fontSize.h2, fontWeight: '800', letterSpacing: -0.35 },
    subheading: { fontSize: fontSize.h3, fontWeight: '700', letterSpacing: -0.15 },
    body: { fontSize: fontSize.body, fontWeight: '600' },
    sub: { fontSize: fontSize.sub, fontWeight: '400' },
    tiny: { fontSize: fontSize.tiny, fontWeight: '400' },
    kicker: { fontSize: fontSize.kicker, fontWeight: '800', letterSpacing: 0.9 },
};
/**
 * The minimum tap target, in points.
 *
 * Apple asks for 44 and Android for 48. This is 48, which is what
 * [Accessibility](../../../docs/ACCESSIBILITY.md) has always said — the 44 that
 * used to be here was drift, and it read as if Apple's floor were the target
 * rather than the smaller of the two industry ones.
 *
 * The number is for eight-year-olds with imprecise fingers, often in a moving
 * car. Where a control is deliberately small — the close cross, a "hear it"
 * chip — the *hit area* is carried out to this without the pixels growing, by
 * the overlay rule in `base.css`.
 */
export const MIN_TAP_TARGET = 48;
export const theme = {
    colors: semantic,
    palette: colors,
    space,
    radius,
    fontSize,
    typography,
    minTapTarget: MIN_TAP_TARGET,
};
//# sourceMappingURL=theme.js.map